import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { MsalProvider } from '@azure/msal-react';
import { PublicClientApplication } from '@azure/msal-browser';
import { initializeMsalConfig } from './services/msalConfig';
import { configService } from './services/configService';
import { useIframeDetection } from './hooks/useIframeDetection';
import { AuthHandler } from './components/AuthHandler';
import { DeviceVerification } from './components/DeviceVerification';
import { Dashboard } from './components/Dashboard';

const AppContent: React.FC = () => {
  const { isInIframe, authMethod } = useIframeDetection();

  return (
    <Router>
      <Routes>
        <Route 
          path="/" 
          element={
            <AuthHandler 
              isInIframe={isInIframe} 
              authMethod={authMethod} 
            />
          } 
        />
        <Route path="/device" element={<DeviceVerification />} />
        <Route path="/dashboard" element={<Dashboard />} />
        
        {/* Catch-all route for unknown paths */}
        <Route path="*" element={
          <div className="not-found">
            <h1>Page Not Found</h1>
            <p>The page you're looking for doesn't exist.</p>
            <button onClick={() => window.location.href = '/'}>
              Go Home
            </button>
            <style>{`
              .not-found {
                min-height: 100vh;
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                text-align: center;
                padding: 2rem;
                background: #f5f5f5;
              }

              .not-found h1 {
                color: #333;
                margin-bottom: 1rem;
              }

              .not-found p {
                color: #666;
                margin-bottom: 2rem;
              }

              .not-found button {
                background: #0078d4;
                color: white;
                border: none;
                padding: 12px 24px;
                border-radius: 6px;
                cursor: pointer;
              }

              .not-found button:hover {
                background: #106ebe;
              }
            `}</style>
          </div>
        } />
      </Routes>
    </Router>
  );
};

const App: React.FC = () => {
  const [msalInstance, setMsalInstance] = useState<PublicClientApplication | null>(null);
  const [configLoading, setConfigLoading] = useState(true);
  const [configError, setConfigError] = useState<string | null>(null);

  useEffect(() => {
    const initializeApp = async () => {
      try {
        // Load configuration first
        await configService.getConfig();
        
        // Initialize MSAL configuration
        const { msalConfig } = await initializeMsalConfig();
        
        // Create MSAL instance
        const instance = new PublicClientApplication(msalConfig);
        setMsalInstance(instance);
      } catch (error) {
        setConfigError(error instanceof Error ? error.message : 'Configuration failed');
      } finally {
        setConfigLoading(false);
      }
    };

    initializeApp();
  }, []);

  if (configLoading) {
    return (
      <div className="config-loading">
        <div className="loading-content">
          <div className="spinner"></div>
          <h2>Loading Application...</h2>
          <p>Fetching configuration from server...</p>
        </div>
        
        <style>{`
          .config-loading {
            min-height: 100vh;
            display: flex;
            align-items: center;
            justify-content: center;
            background: #f5f5f5;
            padding: 2rem;
          }

          .loading-content {
            text-align: center;
            background: white;
            padding: 3rem 2rem;
            border-radius: 12px;
            box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
            max-width: 400px;
          }

          .spinner {
            width: 40px;
            height: 40px;
            border: 4px solid #e3e3e3;
            border-top: 4px solid #0078d4;
            border-radius: 50%;
            animation: spin 1s linear infinite;
            margin: 0 auto 1rem auto;
          }

          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }

          .loading-content h2 {
            color: #333;
            margin: 1rem 0 0.5rem 0;
          }

          .loading-content p {
            color: #666;
            margin: 0;
          }
        `}</style>
      </div>
    );
  }

  if (configError) {
    return (
      <div className="config-error">
        <div className="error-content">
          <h2>Configuration Error</h2>
          <p>Failed to load application configuration:</p>
          <code>{configError}</code>
          <button onClick={() => window.location.reload()}>
            Retry
          </button>
        </div>
        
        <style>{`
          .config-error {
            min-height: 100vh;
            display: flex;
            align-items: center;
            justify-content: center;
            background: #f5f5f5;
            padding: 2rem;
          }

          .error-content {
            text-align: center;
            background: white;
            padding: 3rem 2rem;
            border-radius: 12px;
            box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
            max-width: 500px;
          }

          .error-content h2 {
            color: #d32f2f;
            margin-bottom: 1rem;
          }

          .error-content p {
            color: #666;
            margin-bottom: 1rem;
          }

          .error-content code {
            background: #f5f5f5;
            padding: 0.5rem;
            border-radius: 4px;
            display: block;
            margin-bottom: 2rem;
            color: #d32f2f;
            word-break: break-word;
          }

          .error-content button {
            background: #0078d4;
            color: white;
            border: none;
            padding: 12px 24px;
            border-radius: 6px;
            cursor: pointer;
          }

          .error-content button:hover {
            background: #106ebe;
          }
        `}</style>
      </div>
    );
  }

  if (!msalInstance) {
    return null;
  }

  return (
    <MsalProvider instance={msalInstance}>
      <AppContent />
    </MsalProvider>
  );
};

export default App;